import type { Tone, Audience, MessageType } from '../types'
import { DEFAULT_SKILL_TEMPLATE, TONE_OVERRIDES } from './defaultSkill'

const TONE_MODIFIERS: Record<Tone, string> = TONE_OVERRIDES

const AUDIENCE_CONTEXT: Record<Audience, string> = {
  team: 'Internal teammates who share context. Can use team shorthand and assume shared technical knowledge.',
  manager: 'Your direct manager. Respect hierarchy but be candid. Lead with outcomes and blockers.',
  executive: 'Senior leadership. Lead with business impact. Keep it tight — one paragraph if possible.',
  client: 'External client or stakeholder. Professional, polished, solution-oriented. Zero internal jargon.',
  recruiter: 'Hiring professional. Clear, engaging, highlights value proposition without over-selling.',
  peer: 'A colleague at the same level. Collaborative tone, equal footing, no need to over-explain context.',
}

const MESSAGE_TYPE_RULES: Record<MessageType, string> = {
  slack: 'Conversational and scannable. Short paragraphs or bullets. Avoid walls of text. Emoji used sparingly only if natural. Max 300 words.',
  email: 'Clear opening line, structured body, brief close. Include salutation only if appropriate. Max 400 words.',
  'pr-comment': 'Technical and specific. Reference the relevant code or context directly. Constructive framing — suggest, don\'t criticise. Max 200 words.',
  'performance-review': 'Structured with concrete examples. Forward-looking. Balanced recognition and growth areas. Can be longer.',
  'meeting-notes': 'Bullet-point format. Action items clearly marked with owner if known. Decisions captured, context minimal.',
  linkedin: 'Public professional post. Hook in first line. Value-driven, no internal project names or jargon. Max 300 words.',
}

interface ComposeInput {
  baseSkill?: string
  tone: Tone
  audience: Audience
  messageType: MessageType
  userMessage: string
}

interface ComposedPrompt {
  systemPrompt: string
  userContent: string
}

export function composePrompt({
  baseSkill,
  tone,
  audience,
  messageType,
  userMessage,
}: ComposeInput): ComposedPrompt {
  const template = baseSkill ?? DEFAULT_SKILL_TEMPLATE

  const systemPrompt = template
    .replace('{toneModifier}', TONE_MODIFIERS[tone])
    .replace('{audienceContext}', AUDIENCE_CONTEXT[audience])
    .replace('{messageTypeRules}', MESSAGE_TYPE_RULES[messageType])

  return {
    systemPrompt,
    userContent: userMessage,
  }
}
